<script setup>
import { ref, onMounted } from 'vue';

// API-Basis-URL (für Produktion anpassen)
const API_URL = '/api';

// Musik-Daten
const tracks = ref([]);
const isLoading = ref(true);
const error = ref(null);

// Musik-Einstellungen
const musicSettings = ref({
  traditional_title: 'Traditionell',
  traditional_description: 'Klassische irische Balladen und Folk-Songs, authentisch interpretiert.',
  modern_title: 'Modern',
  modern_description: 'Zeitgenössische Musik mit irischem Flair und einzigartigen Arrangements.',
  live_title: 'Live',
  live_description: 'Energiegeladene Auftritte, die das Publikum zum Tanzen und Mitsingen bringen.',
  spotify_url: '',
  apple_music_url: '',
  youtube_music_url: ''
});

// Lade Musikdaten
const fetchTracks = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await fetch(`${API_URL}/music.php`);
    
    if (!response.ok) {
      throw new Error(`Fehler beim Laden der Musikdaten: ${response.status}`);
    }
    
    const data = await response.json();
    tracks.value = data.tracks || [];
    
  } catch (err) {
    console.error('Fehler beim Laden der Musikdaten:', err);
    error.value = 'Musikdaten konnten nicht geladen werden.';
    
    // Fallback für Entwicklung/Tests
    if (process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        tracks.value = [
          {
            id: 1,
            title: 'Irish Rover',
            album: 'Celtic Memories',
            year: '2023',
            description: 'Traditionelle irische Ballade über ein legendäres Schiff.',
            spotifyLink: 'https://open.spotify.com/track/example1',
            youtubeLink: 'https://youtube.com/watch?v=example1',
            appleMusicLink: 'https://music.apple.com/album/example1',
            isPublished: true
          },
          {
            id: 2,
            title: 'Whiskey in the Jar',
            album: 'Celtic Memories',
            year: '2023',
            description: 'Eines der bekanntesten irischen Volkslieder.',
            spotifyLink: 'https://open.spotify.com/track/example2',
            youtubeLink: 'https://youtube.com/watch?v=example2',
            appleMusicLink: 'https://music.apple.com/album/example2',
            isPublished: true
          }
        ];
        isLoading.value = false;
      }, 500);
    }
  } finally {
    isLoading.value = false;
  }
};

// Lade Musik-Einstellungen
const fetchMusicSettings = async () => {
  try {
    const response = await fetch(`${API_URL}/music_settings.php`);
    
    if (!response.ok) {
      console.error(`Fehler beim Laden der Musik-Einstellungen: ${response.status}`);
      return;
    }
    
    const data = await response.json();
    if (data.success && data.settings) {
      musicSettings.value = {
        ...musicSettings.value,
        ...data.settings
      };
    }
  } catch (err) {
    console.error('Fehler beim Laden der Musik-Einstellungen:', err);
  }
};

// Laden der Daten beim Initialisieren
onMounted(() => {
  fetchTracks();
  fetchMusicSettings();
});
</script>

<template>
  <section id="music" class="music">
    <div class="container">
      <div class="section-header">
        <h2>Unsere Musik</h2>
        <div class="celtic-divider">
          <span></span><svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" /></svg><span></span>
        </div>
      </div>

      <div class="music-categories">
        <div class="category-card">
          <div class="card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="40" height="40">
              <path d="M9 18V5l12-2v13" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="6" cy="18" r="3" stroke-width="2"/>
              <circle cx="21" cy="16" r="3" stroke-width="2"/>
            </svg>
          </div>
          <h3>{{ musicSettings.traditional_title }}</h3>
          <p>{{ musicSettings.traditional_description }}</p>
        </div>
        
        <div class="category-card">
          <div class="card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="40" height="40">
              <circle cx="12" cy="12" r="10" stroke-width="2"/>
              <path d="M12 8l4 4-4 4m-4-4h8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3>{{ musicSettings.modern_title }}</h3>
          <p>{{ musicSettings.modern_description }}</p>
        </div>
        
        <div class="category-card">
          <div class="card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="40" height="40">
              <path d="M15 12c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3z" stroke-width="2"/>
              <path d="M20 12c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3z" stroke-width="2" opacity="0.5"/>
              <path d="M10 12c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3z" stroke-width="2" opacity="0.5"/>
            </svg>
          </div>
          <h3>{{ musicSettings.live_title }}</h3>
          <p>{{ musicSettings.live_description }}</p>
        </div>
      </div>

      <!-- Tracks -->
      <div v-if="isLoading" class="loading-indicator">
        <div class="spinner"></div>
        <p>Musik wird geladen...</p>
      </div>
      
      <div v-else-if="error" class="error-message">
        <p>{{ error }}</p>
      </div>
      
      <div v-else-if="tracks.length === 0" class="no-tracks">
        <p>Derzeit sind keine Musiktitel verfügbar.</p>
      </div>
      
      <div v-else class="music-tracks">
        <h3 class="tracks-title">Unsere Songs</h3>
        <div class="celtic-divider small-divider">
          <span></span><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" /></svg><span></span>
        </div>
        <div class="tracks-grid">
          <div v-for="track in tracks" :key="track.id" class="track-card">
            <div class="track-info">
              <h4 class="track-title">{{ track.title }}</h4>
              <p v-if="track.album || track.year" class="track-meta">
                <span v-if="track.album">{{ track.album }}</span>
                <span v-if="track.album && track.year"> • </span>
                <span v-if="track.year">{{ track.year }}</span>
              </p>
              <p class="track-description">{{ track.description }}</p>
            </div>
            
            <div class="track-links">
              <a v-if="track.spotifyLink" :href="track.spotifyLink" target="_blank" class="track-link spotify" title="Auf Spotify hören">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm5.5 17.3c-.2.3-.6.5-1 .2-2.8-1.7-6.3-2.1-10.5-1.1-.4.1-.8-.2-.9-.6-.1-.4.2-.8.6-.9 4.5-1 8.4-.6 11.6 1.3.4.2.5.7.2 1.1zm1.5-3.3c-.3.4-.8.6-1.2.3-3.2-2-8.1-2.6-11.9-1.4-.5.1-1-.1-1.2-.6-.1-.5.1-1 .6-1.2 4.4-1.3 9.8-.7 13.5 1.7.5.2.6.8.2 1.2zm.1-3.4c-3.8-2.3-10.2-2.5-13.8-1.4-.6.1-1.2-.2-1.3-.8-.2-.6.2-1.2.8-1.4 4.2-1.3 11.1-1 15.5 1.6.5.3.7 1 .4 1.6-.3.4-1 .6-1.6.4z"/>
                </svg>
              </a>
              <a v-if="track.youtubeLink" :href="track.youtubeLink" target="_blank" class="track-link youtube" title="Auf YouTube hören">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm4.441 16.892c-2.102.144-6.784.144-8.883 0-2.276-.156-2.541-1.27-2.558-4.892.017-3.629.285-4.736 2.558-4.892 2.099-.144 6.782-.144 8.883 0 2.277.156 2.541 1.27 2.559 4.892-.018 3.629-.285 4.736-2.559 4.892zm-6.441-7.234l4.917 2.338-4.917 2.346v-4.684z"/>
                </svg>
              </a>
              <a v-if="track.appleMusicLink" :href="track.appleMusicLink" target="_blank" class="track-link apple" title="Auf Apple Music hören">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M12.006 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.374-12-12-12zm3.494 14.375c0 .7-.7 1.2-1.399 1.2-.8 0-1.1-.5-1.1-1.101l-.001-.199c0-1.5-.7-2.2-2.5-2.2-1.2 0-2.1.5-2.1 1.3 0 .699.4 1.1 1.4 1.3l2.1.3c2.3.3 3.6 1.4 3.6 3.4 0 2.199-1.8 3.6-4.5 3.6-2.8 0-4.6-1.5-4.7-3.9v-.1h1.9c.1 1.399 1.2 2.1 2.8 2.1 1.399 0 2.4-.6 2.4-1.5 0-.7-.4-1.2-1.3-1.3l-2.2-.3c-2.2-.3-3.6-1.4-3.6-3.5 0-2.1 1.6-3.4 4.3-3.4 2.699 0 4.3 1.4 4.3 3.7v.1h-1.899zm-8.8-5.775l1.299-2.6h2l-1.9 2.6h-1.399zm5.6 0l1.3-2.6h2l-1.9 2.6h-1.4z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="music-showcase">
        <div class="showcase-intro">
          <h3>Hören Sie uns auf</h3>
          <div class="celtic-divider small-divider">
            <span></span><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" /></svg><span></span>
          </div>
          <p>Unsere Musik ist auf allen gängigen Streaming-Plattformen verfügbar.</p>
        </div>
        <div class="streaming-links">
          <a v-if="musicSettings.spotify_url" :href="musicSettings.spotify_url" target="_blank" class="streaming-link">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm5.5 17.3c-.2.3-.6.5-1 .2-2.8-1.7-6.3-2.1-10.5-1.1-.4.1-.8-.2-.9-.6-.1-.4.2-.8.6-.9 4.5-1 8.4-.6 11.6 1.3.4.2.5.7.2 1.1zm1.5-3.3c-.3.4-.8.6-1.2.3-3.2-2-8.1-2.6-11.9-1.4-.5.1-1-.1-1.2-.6-.1-.5.1-1 .6-1.2 4.4-1.3 9.8-.7 13.5 1.7.5.2.6.8.2 1.2zm.1-3.4c-3.8-2.3-10.2-2.5-13.8-1.4-.6.1-1.2-.2-1.3-.8-.2-.6.2-1.2.8-1.4 4.2-1.3 11.1-1 15.5 1.6.5.3.7 1 .4 1.6-.3.4-1 .6-1.6.4z"/>
            </svg>
            <span>Spotify</span>
          </a>
          <a v-if="musicSettings.apple_music_url" :href="musicSettings.apple_music_url" target="_blank" class="streaming-link">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M12.006 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.374-12-12-12zm3.494 14.375c0 .7-.7 1.2-1.399 1.2-.8 0-1.1-.5-1.1-1.101l-.001-.199c0-1.5-.7-2.2-2.5-2.2-1.2 0-2.1.5-2.1 1.3 0 .699.4 1.1 1.4 1.3l2.1.3c2.3.3 3.6 1.4 3.6 3.4 0 2.199-1.8 3.6-4.5 3.6-2.8 0-4.6-1.5-4.7-3.9v-.1h1.9c.1 1.399 1.2 2.1 2.8 2.1 1.399 0 2.4-.6 2.4-1.5 0-.7-.4-1.2-1.3-1.3l-2.2-.3c-2.2-.3-3.6-1.4-3.6-3.5 0-2.1 1.6-3.4 4.3-3.4 2.699 0 4.3 1.4 4.3 3.7v.1h-1.899zm-8.8-5.775l1.299-2.6h2l-1.9 2.6h-1.399zm5.6 0l1.3-2.6h2l-1.9 2.6h-1.4z"/>
            </svg>
            <span>Apple Music</span>
          </a>
          <a v-if="musicSettings.youtube_music_url" :href="musicSettings.youtube_music_url" target="_blank" class="streaming-link">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm4.441 16.892c-2.102.144-6.784.144-8.883 0-2.276-.156-2.541-1.27-2.558-4.892.017-3.629.285-4.736 2.558-4.892 2.099-.144 6.782-.144 8.883 0 2.277.156 2.541 1.27 2.559 4.892-.018 3.629-.285 4.736-2.559 4.892zm-6.441-7.234l4.917 2.338-4.917 2.346v-4.684z"/>
            </svg>
            <span>YouTube Music</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Bestehende Stile beibehalten und erweitern */
.music {
  padding: 5rem 0;
  background-color: var(--bg-alt);
  position: relative;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-header h2 {
  font-family: 'Playfair Display', serif;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
}

.celtic-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  max-width: 300px;
}

.celtic-divider span {
  flex: 1;
  height: 1px;
  background-color: var(--primary-color);
  opacity: 0.5;
}

.celtic-divider svg {
  margin: 0 15px;
  color: var(--primary-color);
  opacity: 0.7;
}

.tracks-title {
  text-align: center;
  margin: 3rem 0 2rem;
  font-size: 1.75rem;
  color: var(--primary-color);
}

.tracks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.track-card {
  background-color: var(--white);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  transition: var(--transition-normal);
}

.track-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-5px);
}

.track-title {
  font-size: 1.2rem;
  margin: 0 0 0.5rem;
  color: var(--text-color);
}

.track-meta {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0 0 0.75rem;
}

.track-description {
  margin: 0 0 1rem;
  flex-grow: 1;
}

.track-links {
  display: flex;
  gap: 1rem;
  margin-top: auto;
}

.track-link {
  color: var(--text-muted);
  transition: color 0.3s ease;
}

.track-link.spotify:hover {
  color: #1DB954;
}

.track-link.youtube:hover {
  color: #FF0000;
}

.track-link.apple:hover {
  color: #fc3c44;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 132, 61, 0.1);
  border-left-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  background-color: #ffebee;
  color: #e53935;
  padding: 1rem;
  border-radius: var(--radius-sm);
  text-align: center;
  margin: 2rem 0;
}

.no-tracks {
  text-align: center;
  padding: 3rem;
  color: var(--text-muted);
}

/* Bestehende Musik-Komponenten Stile */
.music-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
}

.category-card {
  background-color: var(--white);
  border-radius: var(--radius-md);
  padding: 2rem;
  text-align: center;
  box-shadow: var(--shadow-sm);
  transition: var(--transition-normal);
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-md);
}

.card-icon {
  color: var(--primary-color);
  margin-bottom: 1.5rem;
}

.category-card h3 {
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.category-card p {
  color: var(--text-muted);
}

.music-showcase {
  margin-top: 4rem;
  text-align: center;
}

.small-divider {
  max-width: 200px;
  margin-bottom: 2rem;
}

.small-divider svg {
  width: 18px;
  height: 18px;
}

.showcase-intro {
  margin-bottom: 2rem;
}

.showcase-intro h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.showcase-intro p {
  color: var(--text-muted);
}

.streaming-links {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.streaming-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: var(--text-color);
  text-decoration: none;
  transition: var(--transition-normal);
}

.streaming-link:hover {
  transform: translateY(-5px);
  color: var(--primary-color);
}

.streaming-link svg {
  margin-bottom: 0.5rem;
}

/* Responsive Anpassungen */
@media (max-width: 768px) {
  .tracks-grid {
    grid-template-columns: 1fr;
  }
  
  .streaming-links {
    gap: 1.5rem;
  }
}
</style> 